// ============================================================
// DockerInstallation.jsx
// ============================================================
// Junior-dev Notes:
// - FILE FIX: Image is now correctly IMPORTED from assets to resolve path.
// - UI: Maintained high-contrast Docker Blue (#0DB7ED) branding.
// - Best Practice: All text forced to TEXT_WHITE for legibility.
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import WindowsIcon from "@mui/icons-material/Window";
import AppleIcon from "@mui/icons-material/Apple";
import ComputerIcon from "@mui/icons-material/Computer";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TerminalIcon from "@mui/icons-material/Terminal";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Asset Imports ───────────────────────────────────────── */
// Junior Tip: Always import local images to let Webpack/Vite handle the URL!
import DockerInstallationImg from "../../../assets/images/DockerInstallation.png";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(13, 183, 237, 0.2)",
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
    backgroundColor: BRAND.surface,
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: `1px solid ${BRAND.border}`,
    height: "100%",
}));

const OSButton = styled(Button)(({ theme }) => ({
    justifyContent: "flex-start",
    padding: theme.spacing(2, 3),
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    color: TEXT_WHITE,
    backgroundColor: "rgba(255,255,255,0.02)",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "rgba(13, 183, 237, 0.1)",
        borderColor: DOCKER_BLUE,
    }
}));

export default function DockerInstallation() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HEADER SECTION */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        label="Environment · Module 03"
                        sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px", color: TEXT_WHITE }}>
                        Installation <span style={{ color: DOCKER_BLUE }}>& Setup</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        Before we can ship containers, we need the Docker Engine running.
                        Choose your platform below to begin.
                    </Typography>
                </Box>

                {/* 2. PLATFORM SELECTION */}
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <GlassCard>
                            <Typography variant="h5" fontWeight={800} gutterBottom sx={{ color: TEXT_WHITE }}>
                                Docker Desktop
                            </Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 4 }}>
                                Recommended for Windows and Mac. Includes the Docker Engine,
                                CLI, and a helpful Dashboard GUI for managing images.
                            </Typography>

                            <Stack spacing={2}>
                                <OSButton fullWidth startIcon={<WindowsIcon sx={{ color: "#0078D4" }} />}>
                                    Download for Windows (WSL 2)
                                </OSButton>
                                <OSButton fullWidth startIcon={<AppleIcon sx={{ color: TEXT_WHITE }} />}>
                                    Download for Mac (Intel or Silicon)
                                </OSButton>
                                <OSButton fullWidth startIcon={<ComputerIcon sx={{ color: "#38B000" }} />}>
                                    Docker Desktop for Linux
                                </OSButton>
                            </Stack>
                        </GlassCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card
                            sx={{
                                borderRadius: "16px",
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                border: `1px solid ${BRAND.border}`,
                                height: "100%",
                                position: "relative",
                                overflow: "hidden"
                            }}
                        >
                            <CardMedia
                                component="img"
                                image={DockerInstallationImg} // Junior Tip: Use the imported variable name here!
                                alt="Docker Architecture Diagram"
                                sx={{
                                    height: "100%",
                                    objectFit: "contain", // Changed to contain for architecture diagrams
                                    p: 2,
                                    filter: "brightness(0.9)",
                                    transition: "0.5s ease",
                                    "&:hover": { transform: "scale(1.02)", filter: "brightness(1)" }
                                }}
                            />

                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    width: "100%",
                                    p: 2,
                                    background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
                                    color: "white",
                                    pointerEvents: "none"
                                }}
                            >
                                <Typography variant="caption" sx={{ opacity: 0.8, fontWeight: 600 }}>
                                    Standardized Engine Setup
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* 3. SYSTEM REQUIREMENTS */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, textAlign: "center", color: TEXT_WHITE }}>
                        Prerequisites <span style={{ color: DOCKER_BLUE }}>Checklist</span>
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ color: DOCKER_BLUE, mb: 2 }}>Hardware</Typography>
                                <List dense>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><CheckCircleIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="4GB RAM (Minimum)" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><CheckCircleIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="64-bit Processor" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ color: DOCKER_BLUE, mb: 2 }}>Windows Users</Typography>
                                <List dense>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><CheckCircleIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="Enable WSL 2" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><CheckCircleIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="BIOS Virtualization ON" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ color: DOCKER_BLUE, mb: 2 }}>Verification</Typography>
                                <List dense>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><TerminalIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="docker --version" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                    <ListItem disableGutters>
                                        <ListItemIcon sx={{ minWidth: 30 }}><TerminalIcon sx={{ fontSize: 16, color: DOCKER_BLUE }} /></ListItemIcon>
                                        <ListItemText primary="docker-compose --version" sx={{ color: TEXT_WHITE }} />
                                    </ListItem>
                                </List>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 6 }} />

                {/* 4. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, color: TEXT_WHITE }}>Installed? Let's talk to the Docker Daemon.</Typography>
                    <Link to="/docker-cli" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 6,
                                py: 2,
                                borderRadius: "99px",
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Mastering the CLI
                        </Button>
                    </Link>
                </Box>

            </Container>
        </PageWrapper>
    );
}
